import { TenantConfig } from "./schema.js";
/**
 * The 6 live tenants. Every value here was extracted from production data or the legacy client — nothing invented
 * (one exception: afzrostock's `clientName` is a display name chosen for the owner to confirm, see its entry).
 *  - `realmId` = the PRESERVED legacy int id (must match apps/migrator's realm seed; it is the RLS tenant GUC).
 *  - Every tenant has all THREE environments. Staging is `<realm>.staging.arcreator.at` against `my-dev`
 *    (`KC.staging`), no aliases — `assertIssuersCoverTenants` skips a tenant that lacks the env, so a missing staging
 *    entry would silently drop that tenant from the staging issuer gate (apps/api/src/config/issuer-check.ts).
 *  - Brand ramps + product names: rewrite-docs/design-system-brief.md.
 *  - Hosts/issuers + the `/auth` prefix: rewrite-docs/hosting-dns.md.
 * The dead legacy realm `xrarea` (id 2, 0 users/manuals) is intentionally absent.
 */
/**
 * The shared "XR" palette — `realm.colors` verbatim, used by every tenant EXCEPT takeda (verified byte-identical
 * across xr-area, novonordisk, lumpi and abzbraunau in production).
 * Slots 1–4 are the brand ramp; 5–12 are the neutral scale shared with takeda too.
 */
const XR_PALETTE = [
    "#124468", "#0a588e", "#326f9b", "#2179b9",
    "#c7cdd4", "#9da8b3", "#dde4ed", "#f0f4f8",
    "#2e3c47", "#aabdcc", "#ccd8e5", "#ef7d00",
];
const XR_BRAND = {
    palette: XR_PALETTE,
    // #124468 is 10.23:1 on white — already AA/AAA, so no separate interactive variant is needed there.
    primaryInteractive: "#124468",
    // …but only 1.11:1 on the dark surface (#2e3c47). Lightened 54% toward white -> 4.64:1. Computed, not eyeballed.
    primaryInteractiveDark: "#92a9ba",
};
const KC = {
    local: (realm) => `http://localhost:8081/realms/${realm}`,
    // ⚠ staging/production Keycloak is served under a RELATIVE `/auth` path — the issuer is NOT `<origin>/realms/<realm>`.
    staging: (realm) => `https://my-dev.create.at/auth/realms/${realm}`,
    production: (realm) => `https://my-prod.create.at/auth/realms/${realm}`,
};
/** Legacy sub-tenants lived at `xrarea.arcreator.at/<realm>`; printed QR codes still point there. */
const LEGACY_PREFIX_HOST = "xrarea.arcreator.at";
const raw = [
    {
        realm: "takeda",
        realmId: 1,
        productName: "Knowledge Database", // → the origin of the `kdb-linz.at` vanity domain
        clientName: "Takeda",
        // `defaultLanguage = "de"` — the legacy GLOBAL (`ARLMS_generalVariables.js:14`); NO realm file overrides it,
        // so all five tenants inherit German. Only the LAST fallback: an English browser still gets English.
        defaultLocale: "de",
        brand: {
            // `realm.colors` verbatim (same source as every other tenant). Slots 1–4 are Takeda's own ramp;
            // slots 5–12 are the shared neutral scale, identical to XR_PALETTE.
            // ⚠ OPEN DECISION: the DB says #ef0027 but the legacy shell rendered #ee1100. Kept the DB value so every
            // tenant's palette comes from one source; a brand-guide answer is pending (design-system-brief.md §7).
            palette: [
                "#ef0027", "#fd3b5b", "#748a9d", "#859caf",
                "#c7cdd4", "#9da8b3", "#dde4ed", "#f0f4f8",
                "#2e3c47", "#aabdcc", "#ccd8e5", "#ef7d00",
            ],
            // #ef0027 is 4.47:1 on white → fails AA for body text and white-on-brand buttons. #ed0027 = 4.54:1 and is
            // visually near-identical. (If the brand answer turns out to be #ee1100, its AA-safe partner is #ec1100.)
            primaryInteractive: "#ed0027",
            // #ed0027 is only 2.50:1 on the dark surface; lightened 50% toward white -> 4.55:1. This lightened partner still
            // backs brand TEXT / icons / the focus ring on dark (`--brand-interactive`, `--ring`).
            primaryInteractiveDark: "#f68093",
            // Owner 2026-11-02: Takeda's red is the CONTENT colour (it replaces the orange of content buttons and selection
            // marks); the main colour is slate, so red keeps standing out. Light: palette slot 3 (#748a9d) darkened until white
            // text clears AA (4.53:1, the lightest that still does; the slot itself is 3.58:1). Dark: the neutral palette slot 10 with dark text (5.86:1).
            accentFill: "#ed0027",
            primaryFill: "#63798b",
            primaryFillDark: "#aabdcc",
        },
        featureFlags: {
            sidebarPlatformsVisible: false, // ADR-057: legacy takeda_variables.js
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
            facilityNav: "drilldown", // FN / ADR-014 — owner-requested legacy drill-down nav for existing tenants
            qrCodeLabelTitle: "Knowledge Database", // the ONLY realm that set `qrCodeLabelTitle` (takeda_variables.js:24)
        },
        environments: {
            local: { canonicalHost: "takeda.arcreator.localhost", issuer: KC.local("takeda"), aliases: [] },
            // `idpHint` is the alias as it exists IN THE REALM. auth-tenancy.md documents "create-sso" (from the legacy
            // JS), but the exported realm has no such provider — its aliases are `create-entraid`, `entraid`, `saml`.
            // Verified against infra/keycloak/realms/takeda-realm.json. Deliberately ABSENT for local, where federation
            // is disabled so developers can log in, AND for staging (D-ENTRA): Entra-federated users do not log in on
            // staging and the provider is not required to work on my-dev — a hint naming a disabled/absent provider
            // would break the login page for every staging user (schema.ts `idpHint`). Production keeps it.
            staging: { canonicalHost: "takeda.staging.arcreator.at", issuer: KC.staging("takeda"), aliases: [], qrLegacyDeepLinkBases: ["https://staging.arcreator.at/takeda/"] },
            production: {
                canonicalHost: "kdb-linz.at", // vanity apex — NOT derivable from the realm name
                qrLegacyDeepLinkBases: ["https://kdb-linz.at"], // the legacy `pageURL` (ADR-029)
                issuer: KC.production("takeda"),
                idpHint: "entraid",
                aliases: [{ host: "takeda.arcreator.at" }, { host: "www.kdb-linz.at" }],
            },
        },
    },
    {
        realm: "xr-area",
        realmId: 3,
        productName: "XR-Area",
        clientName: "XR-Area",
        defaultLocale: "de",
        brand: XR_BRAND,
        featureFlags: {
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
            facilityNav: "drilldown", // FN / ADR-014 — owner-requested legacy drill-down nav for existing tenants
        },
        environments: {
            local: { canonicalHost: "xr-area.arcreator.localhost", issuer: KC.local("xr-area"), aliases: [] },
            staging: { canonicalHost: "xr-area.staging.arcreator.at", issuer: KC.staging("xr-area"), aliases: [], qrLegacyDeepLinkBases: ["https://staging.arcreator.at/xr-area/"] },
            production: {
                canonicalHost: "xr-area.arcreator.at",
                qrLegacyDeepLinkBases: ["https://xrarea.arcreator.at"], // the legacy `pageURL` (ADR-029)
                issuer: KC.production("xr-area"),
                // The legacy host label ("xrarea") differs from the realm ("xr-area") — another reason not to string-parse.
                aliases: [{ host: LEGACY_PREFIX_HOST }],
            },
        },
    },
    {
        realm: "novonordisk",
        realmId: 4,
        productName: "Novo Nordisk",
        clientName: "Novo Nordisk",
        defaultLocale: "de",
        brand: XR_BRAND,
        featureFlags: {
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
        },
        environments: {
            local: { canonicalHost: "novonordisk.arcreator.localhost", issuer: KC.local("novonordisk"), aliases: [] },
            staging: { canonicalHost: "novonordisk.staging.arcreator.at", issuer: KC.staging("novonordisk"), aliases: [], qrLegacyDeepLinkBases: [] },
            production: {
                canonicalHost: "novonordisk.arcreator.at",
                qrLegacyDeepLinkBases: ["https://xrarea.arcreator.at/novonordisk"], // the legacy `pageURL` (ADR-029)
                issuer: KC.production("novonordisk"),
                aliases: [{ host: LEGACY_PREFIX_HOST, pathPrefix: "/novonordisk" }],
            },
        },
    },
    {
        realm: "lumpi",
        realmId: 16,
        productName: "XR Knowledgebase",
        clientName: "Lumpi",
        defaultLocale: "de",
        brand: XR_BRAND,
        featureFlags: {
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
        },
        environments: {
            local: { canonicalHost: "lumpi.arcreator.localhost", issuer: KC.local("lumpi"), aliases: [] },
            staging: { canonicalHost: "lumpi.staging.arcreator.at", issuer: KC.staging("lumpi"), aliases: [], qrLegacyDeepLinkBases: [] },
            production: {
                canonicalHost: "lumpi.arcreator.at",
                qrLegacyDeepLinkBases: ["https://xrarea.arcreator.at/lumpi"], // the legacy `pageURL` (ADR-029)
                issuer: KC.production("lumpi"),
                aliases: [{ host: LEGACY_PREFIX_HOST, pathPrefix: "/lumpi" }],
            },
        },
    },
    {
        realm: "abzbraunau",
        realmId: 17,
        productName: "XR Creator",
        clientName: "ABZ Braunau",
        defaultLocale: "de",
        brand: XR_BRAND,
        featureFlags: {
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
        },
        environments: {
            local: { canonicalHost: "abzbraunau.arcreator.localhost", issuer: KC.local("abzbraunau"), aliases: [] },
            staging: { canonicalHost: "abzbraunau.staging.arcreator.at", issuer: KC.staging("abzbraunau"), aliases: [], qrLegacyDeepLinkBases: [] },
            production: {
                canonicalHost: "abzbraunau.arcreator.at",
                qrLegacyDeepLinkBases: ["https://xrarea.arcreator.at/abzbraunau"], // the legacy `pageURL` (ADR-029)
                issuer: KC.production("abzbraunau"),
                aliases: [{ host: LEGACY_PREFIX_HOST, pathPrefix: "/abzbraunau" }],
            },
        },
    },
    {
        // Exists only on my-prod (ADR-035 / rewrite-docs/PARITY-GAPS.md). realmId read from the legacy production DB
        // on 2026-10-23: `SELECT id, name, "deleteManualsAfterDays", deleted_at FROM realm WHERE name = 'afzrostock'`
        // → (18, afzrostock, 30, NULL) — debate/code-k8s-tenant-registry.md. Must equal the literal in
        // apps/migrator/src/migrations/Migration20261022000000_realm_afzrostock.ts.
        realm: "afzrostock",
        realmId: 18,
        productName: "XR Creator", // abzbraunau's XR default — no realm-specific product name exists
        clientName: "AFZ Rostock", // ▶ owner to confirm before the commit — a display name, not a production value
        defaultLocale: "de",
        brand: XR_BRAND, // D-AFZ: the owner keeps afzrostock on the xr-area brand
        featureFlags: {
            ratingsEnabled: true, // legacy default; no realm confirmed OFF (ADR-034 D6)
        },
        // ADR-055: the partners of the AFZ project. The order keeps the owner's decision of 2026-10-30: "Für Später" sits
        // to the right of the done column.
        pmBoard: {
            columns: [
                { key: "offen", title: "Offen", role: "initial" },
                { key: "afz", title: "AFZ Rostock" },
                { key: "uni", title: "Uni" },
                { key: "isv", title: "ISV" },
                { key: "create", title: "Create" },
                { key: "geklaert", title: "Geklärt", role: "done" },
                { key: "spaeter", title: "Für Später", persistent: true },
            ],
            institutes: [
                { key: "create", label: "create" },
                { key: "afz", label: "AFZ Rostock" },
                { key: "isv", label: "ISV" },
                { key: "uni", label: "Uni Rostock" },
            ],
            tags: [
                { key: "create", label: "create", color: "blue" },
                { key: "afz", label: "AFZ Rostock", color: "green" },
                { key: "isv", label: "ISV", color: "orange" },
                { key: "uni", label: "Uni Rostock", color: "purple" },
            ],
            openToAllUsers: true,
        },
        environments: {
            local: { canonicalHost: "afzrostock.arcreator.localhost", issuer: KC.local("afzrostock"), aliases: [] },
            staging: { canonicalHost: "afzrostock.staging.arcreator.at", issuer: KC.staging("afzrostock"), aliases: [], qrLegacyDeepLinkBases: [] },
            production: {
                canonicalHost: "afzrostock.arcreator.at",
                issuer: KC.production("afzrostock"),
                // No aliases and no legacy QR base: the realm was created after the move off the shared
                // `xrarea.arcreator.at/<realm>` prefix host, so no printed code points anywhere else.
                aliases: [],
                qrLegacyDeepLinkBases: [],
            },
        },
    },
];
/** Parsed + validated at import — a malformed tenant config fails immediately, not at first request. */
export const tenants = raw.map((t) => TenantConfig.parse(t));
//# sourceMappingURL=tenants.js.map